<template>
  <SCADialog
    :title="resolvedTitle"
    size="small"
    padding="large"
    header-appearance="plain"
    @close="close('cancel')"
    @confirm="close('confirm')"
  >
    <div v-if="bodyMessage" :class="$style.message">
      <span>{{ bodyMessage.primary }}</span>
      <span v-if="bodyMessage.secondary">&nbsp;{{ bodyMessage.secondary }}</span>
    </div>

    <slot name="content" :set-payload="setPayload" :close="close"></slot>

    <component
      :is="customComponent"
      v-if="customComponent"
      v-bind="customComponentProps"
      :set-payload="setPayload"
      @close="close"
    />

    <template #footer>
      <div :class="$style.actions">
        <div :class="$style.left">
          <SCAButton
            v-if="type === 'alert'"
            size="small"
            :icon-left="confirmIcon ?? 'phosphor/check'"
            @click="close('confirm')"
          >
            {{ confirmationText ?? $t("common.action.ok") }}
          </SCAButton>

          <SCAButton
            v-else-if="type === 'save'"
            :icon-left="confirmIcon ?? 'phosphor/floppy-disk'"
            size="small"
            @click="close('confirm')"
          >
            {{ confirmationText ?? $t("common.action.save") }}
          </SCAButton>

          <SCAButton
            v-else-if="type === 'delete'"
            :icon-left="confirmIcon ?? 'phosphor/trash'"
            size="small"
            intent="destructive"
            @click="close('confirm')"
          >
            {{ confirmationText ?? $t("common.action.delete") }}
          </SCAButton>

          <SCAButton
            v-else-if="type === 'discard'"
            size="small"
            intent="destructive"
            :icon-left="confirmIcon ?? discardIcon ?? 'phosphor/broom'"
            @click="close('confirm')"
          >
            {{ confirmationText ?? $t("settings.tableBar.label.discard") }}
          </SCAButton>

          <SCAButton v-if="type === 'save'" size="small" appearance="secondary" @click="close('abort')">
            {{ discardText ?? $t("settings.tableBar.label.discard") }}
          </SCAButton>

          <SCAButton
            v-if="type === 'delete' || type === 'discard' || type === 'alert'"
            size="small"
            appearance="secondary"
            @click="close('cancel')"
          >
            {{ discardText ?? $t("common.action.cancel") }}
          </SCAButton>
        </div>

        <div :class="$style.right">
          <SCAButton v-if="type === 'save'" size="small" appearance="secondary" @click="close('cancel')">
            {{ cancelText ?? $t("common.action.continueEditing") }}
          </SCAButton>
        </div>
      </div>
    </template>
  </SCADialog>
</template>

<script setup lang="ts">
import type { Component } from "vue";
import { computed, ref } from "vue";
import { useModal } from "vue-haystack";
import { useI18n } from "vue-i18n";

import type { IconName } from "../icons/iconNames.generated";
import SCAButton from "../SCAButton.vue";
import SCADialog from "../SCADialog.vue";

const { t: $t } = useI18n();

const props = withDefaults(
  defineProps<{
    title?: string;
    text?: string;
    showDeleteSecondaryText?: boolean;
    confirmationText?: string;
    cancelText?: string;
    discardText?: string;
    discardIcon?: IconName;
    confirmIcon?: IconName;
    type?: "save" | "delete" | "discard" | "alert";
    customComponent?: Component;
    customComponentProps?: Record<string, unknown>;
  }>(),
  {
    type: "alert",
    title: undefined,
    text: undefined,
    showDeleteSecondaryText: true,
    confirmationText: undefined,
    cancelText: undefined,
    discardText: undefined,
    discardIcon: undefined,
    confirmIcon: undefined,
    customComponent: undefined,
    customComponentProps: () => ({}),
  },
);

const resolvedTitle = computed(
  () =>
    props.title ??
    {
      alert: $t("common.label.pleaseConfirmAction"),
      save: $t("common.action.saveChanges"),
      discard: $t("settings.tableBar.label.discardChanges2"),
      delete: $t("common.action.deleteRecord"),
    }[props.type],
);

const bodyMessage = computed(() => {
  const { text, type, showDeleteSecondaryText } = props;

  if (text) {
    return {
      primary: text,
      secondary: type === "delete" && showDeleteSecondaryText ? $t("common.label.stepCannotUndone") : undefined,
    };
  }

  if (type === "discard") {
    return { primary: $t("common.message.confirmDiscardChanges"), secondary: undefined };
  }

  if (type === "save") {
    return { primary: $t("common.label.unsavedChangesLike"), secondary: undefined };
  }

  return undefined;
});

const modal = useModal();

const dialogPayload = ref<unknown>(undefined);

const setPayload = (data: unknown) => {
  dialogPayload.value = data;
};

const close = (action: "confirm" | "cancel" | "abort", directPayload?: unknown) => {
  const finalPayload = directPayload !== undefined ? directPayload : dialogPayload.value;

  if (finalPayload) {
    modal.close({
      action,
      payload: finalPayload,
    });
  } else {
    modal.close(action);
  }
};
</script>

<style module lang="scss">
.actions {
  display: flex;
  justify-content: space-between;
  width: 100%;

  .left {
    display: flex;
    gap: var(--space-component-padding-small);
  }

  .right {
    display: flex;
  }
}

.message {
  white-space: pre-line;
}
</style>
